/**
 * `CompetenceDetail` — pagina di dettaglio di una singola competenza,
 * raggiungibile dalla Dashboard cliccando una `CompetencyMatchCard`.
 *
 * La struttura ricalca la vista "competenza selezionata" dell'`AssessmentWizard`
 * (intestazione con nome competenza + azione per tornare indietro), ma al posto
 * del form di autovalutazione mostra la **stessa tabella** della pagina
 * `SkillList`, tramite il componente condiviso {@link SkillAssessmentTable},
 * filtrata alle sole skill della competenza corrente e con una colonna extra
 * "Peso" (peso della skill sulla competenza, in sola lettura).
 *
 * Come nella pagina `SkillList`, il Self-score è **editabile al volo**: il
 * componente condiviso gestisce l'inline edit e il salvataggio via
 * `PUT /api/assessments/me/:skillId/self`.
 *
 * Dati:
 *  - `GET /api/competences` → risolve la competenza dall'`:competenceId` e il
 *    suo elenco di `skillIds` (con i pesi);
 *  - `GET /api/skills` → nomi delle skill (anche quelle non ancora valutate);
 *  - `GET /api/assessments/me` → i voti dell'utente (Self/AI/Reviewer/Manager);
 *  - `GET /api/dashboard/me` → Copertura/Proficiency della competenza per il
 *    donut e le metriche in testata.
 */
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  DashboardDto,
  SkillDto,
} from '../shared/dto';
import {
  DEFAULT_SKILL_WEIGHT,
  LIVELLO_VALUES,
  MAX_SKILL_WEIGHT,
  type Livello,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { useAigor } from '../components/AigorDrawer';
import { SkillAssessmentTable } from '../components/SkillAssessmentTable';
import { CompetenceInfoPanel } from '../components/CompetenceInfoPanel';
import {
  Alert,
  Button,
  DotRating,
  EmptyState,
  Loading,
  PageHeader,
  type Column,
  type SortDirection,
} from '../components/ui';

/** Etichetta descrittiva del peso, allineata a `SkillWeightEditor`. */
const WEIGHT_HINT: Record<number, string> = {
  1: 'Marginale',
  2: 'Bassa',
  3: 'Media',
  4: 'Alta',
  5: 'Essenziale',
};

/** Comparatore per la colonna "Peso" (peso della skill sulla competenza). */
function makeWeightComparator(weightOf: (skillId: string) => number) {
  return (a: AssessmentView, b: AssessmentView, direction: SortDirection): number => {
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (weightOf(a.skillId) - weightOf(b.skillId));
  };
}

/**
 * Converte la Proficiency `0..100` nel {@link Livello} discreto corrispondente
 * (`0% → e-0 … 100% → e-5`), arrotondando. Stessa mappatura del backend
 * (`jobPositioningService.percentToLivello`) usata per stimare il "Livello
 * posseduto" dall'utente su una competenza a partire dalla sua Proficiency.
 */
function proficiencyToLivello(percent: number | null): Livello | null {
  if (percent === null) return null;
  const clamped = Math.min(100, Math.max(0, percent));
  const rank = Math.round((clamped / 100) * (LIVELLO_VALUES.length - 1));
  return LIVELLO_VALUES[rank];
}

export default function CompetenceDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { competenceId } = useParams<{ competenceId: string }>();

  // Provenienza opzionale propagata dal chiamante come `state.from` (es. la
  // professione da cui si è aperta la competenza): se presente, il tasto
  // "indietro" torna lì invece che al default ("Il mio CV", `/assessment`).
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const backFrom =
    typeof fromState === 'string' && fromState !== '' ? fromState : null;
  // Etichetta del tasto "indietro" coerente con la provenienza: una professione
  // (`/assessment/jobs/...`) riporta "alla professione"; ogni altra provenienza
  // nota (o assente) riporta "al mio CV".
  const backLabel =
    backFrom !== null && backFrom.startsWith('/assessment/jobs/')
      ? '← Torna alla professione'
      : '← Torna al mio CV';
  const { me } = useAuth();
  const { open: openAigor } = useAigor();

  // Colonna e tasto AI visibili solo se l'AI è abilitata per l'utente (Req. 7.4).
  const aiEnabled = me?.aiEnabled === true;

  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: ['competences'],
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  const assessmentsQuery = useQuery<AssessmentView[]>({
    queryKey: ['assessments', 'me'],
    queryFn: () => api.get<AssessmentView[]>('/api/assessments/me'),
  });

  // Match/rank della competenza: dallo stesso payload della Dashboard
  // (`GET /api/dashboard/me`). Riusiamo la query key ['dashboard','me'].
  const dashboardQuery = useQuery<DashboardDto>({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.get<DashboardDto>('/api/dashboard/me'),
  });

  const competence = useMemo(
    () => (competencesQuery.data ?? []).find((c) => c.id === competenceId) ?? null,
    [competencesQuery.data, competenceId],
  );

  // Mappa skillId → SkillDto per selfAssessable/aiAssessable (colonna Azioni).
  const skillById = useMemo(
    () => new Map((skillsQuery.data ?? []).map((s) => [s.id, s])),
    [skillsQuery.data],
  );

  // Match/rank della competenza corrente, se presente nel payload dashboard.
  const match = useMemo(
    () =>
      (dashboardQuery.data?.competencyMatches ?? []).find(
        (m) => m.competenceId === competenceId,
      ) ?? null,
    [dashboardQuery.data, competenceId],
  );

  // Righe della tabella: le skill della competenza corrente (nell'ordine di
  // `skillIds`), arricchite con i voti dell'utente quando presenti.
  const rows = useMemo<AssessmentView[]>(() => {
    if (!competence) return [];
    const byId = new Map((skillsQuery.data ?? []).map((s) => [s.id, s]));
    const assessmentBySkill = new Map(
      (assessmentsQuery.data ?? []).map((a) => [a.skillId, a]),
    );
    return competence.skillIds
      .map((skillId) => {
        const existing = assessmentBySkill.get(skillId);
        if (existing) return existing;
        const skill = byId.get(skillId);
        if (!skill) return null;
        return {
          skillId,
          skillName: skill.name,
          selfInterest: null,
          aiLevel: null,
          reviewerLevelRank: null,
          reviewerCount: 0,
          reviewerUniformLevel: null,
          managerLevelRank: null,
          managerCount: 0,
          managerUniformLevel: null,
          livelloSelf: null,
          yearsOfExperience: null,
          score: null,
          // Skill non valutata: nessuna certificazione contribuisce allo Score.
          certificationCount: 0,
          // Skill non valutata: nessuna revisione.
          reviews: [],
        } satisfies AssessmentView;
      })
      .filter((r): r is AssessmentView => r !== null);
  }, [competence, skillsQuery.data, assessmentsQuery.data]);

  // Ritorno: alla provenienza esplicita (`state.from`, es. la professione) se
  // presente, altrimenti alla pagina "Il mio CV" (`/assessment`), che è la
  // "dashboard" personale.
  const backToMyCv = () => {
    void navigate(backFrom ?? '/assessment');
  };

  // Apre il wizard di assessment già posizionato su questa competenza.
  const goToAssessment = () => {
    if (!competenceId) return;
    void navigate(`/assessment/wizard?competenceId=${encodeURIComponent(competenceId)}`);
  };

  // Peso (1..5) di una skill sulla competenza corrente; default 1 se assente.
  const skillWeights = competence?.skillWeights;
  const weightOf = useMemo(() => {
    const map = skillWeights ?? {};
    return (skillId: string) => map[skillId] ?? DEFAULT_SKILL_WEIGHT;
  }, [skillWeights]);

  // Colonna extra "Peso": specifica del dettaglio competenza, iniettata nella
  // tabella condivisa subito prima della colonna "Livello".
  const extraColumns = useMemo<Column<AssessmentView>[]>(
    () => [
      {
        key: 'weight',
        header: 'Peso',
        sortable: true,
        sortComparator: makeWeightComparator(weightOf),
        // Peso della skill sulla competenza (1..5), a pallini in sola lettura.
        render: (row) => (
          <DotRating
            value={weightOf(row.skillId)}
            onChange={() => {}}
            readOnly
            size="sm"
            max={MAX_SKILL_WEIGHT}
            label={`Peso di ${row.skillName}`}
            optionLabel={(n) => `${n} — ${WEIGHT_HINT[n] ?? ''}`.trim()}
          />
        ),
        className: 'skill-list__score-col',
      },
    ],
    [weightOf],
  );

  // ----- Rendering -----

  if (competencesQuery.isLoading) {
    return (
      <section aria-labelledby="competence-detail-title">
        <h1 id="competence-detail-title">Competenza</h1>
        <Loading>Caricamento della competenza…</Loading>
      </section>
    );
  }

  if (competencesQuery.isError) {
    const message =
      competencesQuery.error instanceof Error
        ? competencesQuery.error.message
        : 'Impossibile caricare la competenza.';
    return (
      <section aria-labelledby="competence-detail-title">
        <h1 id="competence-detail-title">Competenza</h1>
        <Alert tone="error">{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToMyCv}>
            {backLabel}
          </Button>
        </div>
      </section>
    );
  }

  if (competence === null) {
    return (
      <section aria-labelledby="competence-detail-title">
        <h1 id="competence-detail-title">Competenza</h1>
        <EmptyState role="status">
          La competenza richiesta non esiste o non è più disponibile.
        </EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToMyCv}>
            {backLabel}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="competence-detail" aria-labelledby="competence-detail-title">
      <PageHeader
        title="Dettaglio competenza"
        titleId="competence-detail-title"
        subtitle="Le skill della competenza con il tuo Self, modificabile al volo."
        actions={
          <div className="competence-detail__header-actions">
            <Button type="button" variant="ghost" onClick={backToMyCv}>
              {backLabel}
            </Button>
            <Button type="button" variant="primary" onClick={goToAssessment}>
              Vai alle competenze
            </Button>
          </div>
        }
      />

      {/* Card principale (hero, con descrizione e mission) + 5 card dei Livelli
          e-CF. Il donut mostra la Copertura e la barra la Proficiency. */}
      <CompetenceInfoPanel
        competence={competence}
        badge="Competenza"
        donutValue={match ? match.coveragePercent : 0}
        donutLabel="Copertura"
        metricValue={match ? match.proficiencyPercent : null}
        metricLabel="Proficiency"
        ownedLevel={proficiencyToLivello(match ? match.proficiencyPercent : null)}
      />

      <SkillAssessmentTable
        rows={rows}
        // Oltre alla lista dei voti, invalida anche il dashboard così il donut
        // del rank/match in alto si aggiorna subito dopo il salvataggio del Self.
        additionalInvalidateKeys={[['dashboard', 'me']]}
        aiEnabled={aiEnabled}
        skillById={skillById}
        extraColumns={extraColumns}
        onOpenAigor={(row) =>
          openAigor(row.skillId, {
            skillName: row.skillName,
            skillDescription: skillById.get(row.skillId)?.description,
            userName: me?.displayName,
            existingAiLevel: row.aiLevel,
          })
        }
        loading={skillsQuery.isLoading || assessmentsQuery.isLoading}
        error={
          skillsQuery.isError || assessmentsQuery.isError
            ? 'Impossibile caricare le skill della competenza.'
            : null
        }
        emptyMessage="Questa competenza non ha skill collegate."
        defaultSort={{ key: 'skillName', direction: 'asc' }}
        paginated={false}
      />
    </section>
  );
}
